import { Box, Typography, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import Grid from '@mui/material/Grid2';
import { useGetAgencyConfiguration, useUpdateAgencyConfiguration } from 'apis/agency/configuration/hook';
import InfoIcon from 'assets/icons/InfoIcon';
import Input from 'components/Common/Input';
import PasswordInput from 'components/Common/PasswordInput';
import { useForm } from 'react-hook-form';
import { smtpSchema } from 'utils/schema/validator/AgencyConfigureValidation';
import SmtpConfigLoader from './components/Loader';
import { zodResolver } from '@hookform/resolvers/zod';

export default function Smtp() {
  const theme = useTheme();
  const { data: configuration, isLoading } = useGetAgencyConfiguration();
  const { mutateAsync: updateConfiguration, isPending } = useUpdateAgencyConfiguration();

  const { handleSubmit, control, reset } = useForm({
    resolver: zodResolver(smtpSchema),
    values: { configuration: configuration }
  });

  const onSubmit = (data: any) => {
    updateConfiguration(data);
  };

  if (isLoading) {
    return <SmtpConfigLoader />;
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
        Email (SMTP)
      </Typography>
      <Typography color='secondary' variant='body2' mt={0.5} sx={{ fontSize: { xs: '14px', lg: 16 } }}>
        Add your SMTP credentials to send emails through your domain for better deliverability and trust.
      </Typography>
      <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
        <Grid
          container
          spacing={{ xs: 2, lg: 2 }}
          bgcolor={theme.palette.background.paper}
          padding={{ xs: 2, lg: 3 }}
          borderRadius='4px'
          border='1px solid #ECECEC'
        >
          <Box
            sx={{
              backgroundColor: '#F0F6FF',
              border: '1px solid #D0E3FF',
              borderRadius: 2,
              padding: { xs: 1, lg: 1 },
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Box
              component='span'
              sx={{
                width: { xs: 20, lg: 24 },
                height: { xs: 20, lg: 24 },
                mt: '2px',
                color: '#1E66F5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <InfoIcon />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography fontWeight={500} mb={0.5} color='#1E66F5' sx={{ fontSize: { xs: '0.875rem', lg: '1rem' } }}>
                Email Service Information
              </Typography>
              <Typography color='#1E66F5' sx={{ fontSize: { xs: '12px', lg: 14 } }}>
                This SMTP configuration will be used for sending all platform emails including notifications,
                verifications, and other transactional communications.
              </Typography>
            </Box>
          </Box>
          <Grid size={12} my={0.5}>
            <Input name='configuration.smtp_username' control={control} fullWidth label='SMTP Username' />
          </Grid>
          <Grid size={12} my={0.5}>
            <PasswordInput name='configuration.smtp_password' control={control} fullWidth label='SMTP Password' />
          </Grid>
          <Grid size={12} my={0.5}>
            <Input name='configuration.smtp_address' control={control} fullWidth label='SMTP Address' />
          </Grid>
          <Grid size={12} my={0.5}>
            <Input name='configuration.smtp_domain' control={control} fullWidth label='SMTP Domain' />
          </Grid>
          <Grid size={12} my={0.5}>
            <Input name='configuration.smtp_port' type='number' control={control} fullWidth label='SMTP Port' />
          </Grid>
          <Grid size={12} my={0.5}>
            <Input name='configuration.smtp_from' control={control} fullWidth label='SMTP From Email' />
          </Grid>
          <Grid size={12} my={0.5}>
            <Input name='configuration.smtp_from_name' control={control} fullWidth label='SMTP From Name' />
          </Grid>
        </Grid>
        <Box
          mt={{ xs: 2, lg: 3 }}
          display='flex'
          flexDirection={{ xs: 'column-reverse', sm: 'row' }}
          gap={1}
          justifyContent='end'
        >
          <Button
            color='secondary'
            variant='outlined'
            sx={{
              border: '1px solid #ECECEC',
              bgcolor: 'white',
              width: { xs: '100%', sm: 'auto' }
            }}
            onClick={() => reset()}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            fullWidth
            isPending={isPending}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Configure
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
